import React, { memo, useEffect, useState } from 'react';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import { App, Button, Card, Spin, Typography } from 'antd';

import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import HelperPage from 'v2/components/Shared/HelperPage';
import TopPage from 'v2/components/Shared/TopPage';
import Truncate from 'v2/components/Shared/TruncateText';
import { dateFormat } from 'v2/formatters/date';
import configAPIService from 'v2/services/configAPI';
import eduzzAccountsService from 'v2/services/eduzzAccount';

import { Icon } from '@eduzz/ui-antd-theme';

import APIInativationAlert from './APIInactivationAlert';
import { NewApiKeyDialog } from './newApiKeyDialog';

export const ConfigAPI = memo(() => {
  const { message } = App.useApp();

  useObservable(() => configAPIService.loadApiKey(), []);
  useObservable(() => configAPIService.loadOriginKey(), []);

  const [showApiKey, setShowApiKey] = useState(false);
  const [open, setOpen] = useState(false);

  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);
  const [loadingApiKey] = useObservable(() => configAPIService.isLoadingApiKey(), []);
  const [apiKey] = useObservable(() => configAPIService.getApiKey(), []);
  const [loadingOriginKey] = useObservable(() => configAPIService.isLoadingOriginKey(), []);
  const [originKey] = useObservable(() => configAPIService.getOriginKey(), []);

  const [updateOriginKey] = useCallbackObservable(
    () =>
      configAPIService.updateOriginKey().pipe(
        tap(() => {
          message.success('API key atualizada com sucesso');
        }),
        catchError(() => {
          message.error('Ocorreu um erro ao tentar gerar uma nova Origin Key');
          return of(true);
        })
      ),
    [message]
  );

  const onClose = () => {
    setShowApiKey(true);
    setOpen(false);
  };

  useEffect(() => () => setShowApiKey(false), []);

  const getShowKey = () => {
    if (apiKey?.deprecated_api_key) return apiKey.deprecated_api_key;
    if (showApiKey && apiKey?.api_key) return apiKey?.api_key;
    if (apiKey?.api_key_last_digits) return `*********${apiKey?.api_key_last_digits}`;
    return '*************';
  };

  const currentApiKey = getShowKey();

  const listKeys = [
    {
      name: 'Public Key',
      key: `${user?.id}`,
      canCopy: true,
      generated_at: '',
      loading: false
    },
    {
      name: 'API Key',
      key: currentApiKey,
      canCopy: showApiKey,
      generated_at: apiKey?.api_key_generated_at,
      loading: loadingApiKey
    },
    {
      name: 'Origin Key',
      key: originKey?.origin_secret ?? 'Não gerada',
      canCopy: !!originKey?.origin_secret,
      generated_at: originKey?.origin_secret_generated_at,
      loading: loadingOriginKey
    }
  ];

  const handleCopy = (value: string) => {
    navigator.clipboard.writeText(value);
    message.success('Chave copiada');
  };

  return (
    <>
      <APIInativationAlert />

      <TopPage
        title='API Eduzz'
        extra='Gerencie suas chaves de integração'
        disableAlerts
        additionalContent={<HelperPage id='config-api-button' title='Configuração de API' name='config_api' />}
      />

      <div className='mx-auto flex w-full max-w-xl flex-col gap-4'>
        <Card>
          <div className='flex flex-col'>
            {listKeys.map(keyItem => (
              <div
                key={keyItem.name}
                className='flex items-center justify-between gap-4 border-b border-(--uizz-antd-color-border-secondary) py-3 last:border-b-0'
              >
                <div className='min-w-0'>
                  <Typography.Text strong className='mb-1.5 block'>
                    {keyItem.name}
                  </Typography.Text>
                  {keyItem.loading ? (
                    <Spin size='small' />
                  ) : (
                    <Typography.Text className='text-xs'>
                      <Truncate count={40} final='...' title={keyItem.key}>
                        {keyItem.key}
                      </Truncate>
                    </Typography.Text>
                  )}
                  {!keyItem.loading && keyItem.generated_at && (
                    <Typography.Text type='secondary' className='mt-1 block text-xs'>
                      Criado em: {dateFormat(keyItem.generated_at, 'dd/MM/yyyy HH:mm')}
                    </Typography.Text>
                  )}
                </div>
                {keyItem.canCopy && (
                  <Button
                    type='text'
                    shape='circle'
                    aria-label={`Copiar ${keyItem.name}`}
                    icon={<Icon.Copy size={16} />}
                    onClick={() => handleCopy(keyItem.key)}
                  />
                )}
              </div>
            ))}
          </div>
        </Card>

        <Typography.Text type='secondary'>* Não compartilhe essas informações com ninguém!</Typography.Text>

        <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
          <Button
            type='primary'
            block
            icon={<Icon.RefreshCw size={16} />}
            loading={loadingOriginKey}
            disabled={loadingOriginKey}
            onClick={updateOriginKey}
          >
            Gerar nova Origin key
          </Button>
          <Button
            type='primary'
            block
            icon={<Icon.RefreshCw size={16} />}
            loading={loadingApiKey}
            disabled={loadingApiKey}
            onClick={() => setOpen(true)}
          >
            Gerar nova API key
          </Button>
        </div>
      </div>

      <NewApiKeyDialog open={open} onClose={onClose} onConfirm={onClose} />
    </>
  );
});
